import React, { useMemo } from 'react'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import _ from 'lodash'
import { SITE_NAME } from '../../../../constants'
import { Button, Grid } from '@mui/material'
import FormikTextField from '@components/Formik/FormikTextField'
import { CountryCodeEnum, StoreTypeEnum } from '../../../../generated/gqlTypes'
import FormikAutocomplete from '@components/Formik/FormikAutocomplete'
import { ISO_COUNTRIES } from '../../../misc/country'
import { countryToFlag } from '../../../utils/misc'
import FormikCheckbox from '@components/Formik/FormikCheckbox'
import { ErrorFragment } from '@graphql/fragments/__generated__/Error.generated'
import FormikErrors from '@components/Formik/FormikErrors'
import { useTranslation } from 'react-i18next'
import { FORM_ERRORS_MAPPING } from '../../../../mappings'

interface Option {
  label: string
  value: string | number | null
}

export interface WholesaleRegistrationFormData {
  subscribe: boolean

  email: string
  storeName: string
  storeType: StoreTypeEnum
  storeYear: number | null
  websiteUrl: string
  instagramUrl: string
  facebookUrl: string
  brands: string[]
  storeDescription: string[]

  firstName: string
  lastName: string
  city: string
  country: CountryCodeEnum
}

interface Props {
  onSubmit(values: WholesaleRegistrationFormData): Promise<void>

  errors: ErrorFragment[]
  email: string
}

const WholesaleRegistrationForm: React.FC<Props> = ({ onSubmit, errors, email }) => {
  const { t } = useTranslation()
  const schema = useMemo(
    () =>
      yup.object().shape<WholesaleRegistrationFormData>({
        subscribe: yup.boolean().oneOf([true], FORM_ERRORS_MAPPING(t, 'mustBeChecked')),

        email: yup
          .string()
          .email(FORM_ERRORS_MAPPING(t, 'invalidEmail'))
          .required(FORM_ERRORS_MAPPING(t, 'valueIsRequired')),
        storeName: yup
          .string()
          .required(FORM_ERRORS_MAPPING(t, 'valueIsRequired'))
          .max(128, FORM_ERRORS_MAPPING(t, 'maxLength', { max: 128 })),
        storeType: yup.string<StoreTypeEnum>().required(FORM_ERRORS_MAPPING(t, 'valueIsRequired')),
        storeYear: yup.number().nullable(),
        websiteUrl: yup.string().max(256, FORM_ERRORS_MAPPING(t, 'maxLength', { max: 256 })),
        instagramUrl: yup
          .string()
          .max(256, FORM_ERRORS_MAPPING(t, 'maxLength', { max: 256 }))
          .nullable(),
        facebookUrl: yup
          .string()
          .max(256, FORM_ERRORS_MAPPING(t, 'maxLength', { max: 256 }))
          .nullable(),
        brands: yup
          .array()
          .of(yup.string())
          .min(1, FORM_ERRORS_MAPPING(t, 'tooShortWithValue', { min: 1 })),
        storeDescription: yup
          .array()
          .of(yup.string())
          .min(1, FORM_ERRORS_MAPPING(t, 'tooShortWithValue', { min: 1 })),

        firstName: yup
          .string()
          .required(FORM_ERRORS_MAPPING(t, 'valueIsRequired'))
          .max(256, FORM_ERRORS_MAPPING(t, 'maxLength', { max: 256 })),
        lastName: yup
          .string()
          .required(FORM_ERRORS_MAPPING(t, 'valueIsRequired'))
          .max(256, FORM_ERRORS_MAPPING(t, 'maxLength', { max: 256 })),
        country: yup.string<CountryCodeEnum>().required(FORM_ERRORS_MAPPING(t, 'valueIsRequired')),
        city: yup
          .string()
          .required(FORM_ERRORS_MAPPING(t, 'valueIsRequired'))
          .max(256, FORM_ERRORS_MAPPING(t, 'maxLength', { max: 256 })),
      }),
    [t]
  )
  const initialValues = useMemo<WholesaleRegistrationFormData>(
    () => ({
      subscribe: false,

      email: email,
      storeName: '',
      storeType: StoreTypeEnum.Online,
      storeYear: null,
      websiteUrl: '',
      instagramUrl: '',
      facebookUrl: '',
      brands: [],
      storeDescription: [],

      firstName: '',
      lastName: '',
      country: CountryCodeEnum.Us,
      city: '',
    }),
    [email]
  )

  const BRANDS: Option[] = [
    { label: t('categoryCrossStitch', 'Cross-Stitch'), value: 'Cross-Stitch' },
    { label: t('categoryDiamondPainting', 'Diamond Painting'), value: 'Diamond Painting' },
    { label: t('categoryBeading', 'Beading'), value: 'Beading' },
    { label: t('categoryEmbroidery', 'Embroidery'), value: 'Embroidery' },
  ]

  const STORE_TYPES: Option[] = [
    { label: t('formOptions.storeType.onlineOnly', 'Online only'), value: StoreTypeEnum.Online },
    { label: t('formOptions.storeType.brickAndMortar', 'Brick and Mortar'), value: StoreTypeEnum.BrickAndMortar },
    { label: t('formOptions.storeType.bothOnline', 'Both Online and B & M'), value: StoreTypeEnum.BothOnline },
  ]

  const YEARS: Option[] = [
    { label: t('formOptions.storeYear.notOpenYet', 'Not Open Yet'), value: '' },
    ..._.range(new Date().getFullYear(), 1989, -1).map(year => ({ label: year.toString(), value: year })),
    { label: t('formOptions.storeYear.before1990', 'Before 1990'), value: 1989 },
  ]

  const STORE_DESCRIPTIONS: Option[] = [
    { label: t('formOptions.storeDescription.toys', 'Toys'), value: 'Toys' },
    { label: t('formOptions.storeDescription.childrenBoutique', 'Children Boutique'), value: 'Children Boutique' },
    { label: t('formOptions.storeDescription.lifestyle', 'Lifestyle & Home Decor'), value: 'Lifestyle & Home Decor' },
    { label: t('formOptions.storeDescription.artAndCraft', 'Art & Craft'), value: 'Art & Craft' },
    {
      label: t('formOptions.storeDescription.museumAndInstitution', 'Museum & Institution'),
      value: 'Museum & Institution',
    },
    {
      label: t('formOptions.storeDescription.generalAndDepartmentStore', 'General & Department Store'),
      value: 'General & Department Store',
    },
    { label: t('formOptions.storeDescription.other', 'Other'), value: 'Other' },
  ]
  const SKIP_COUNTRIES = ['RU', 'BY']
  const isoCountriesOptions = ISO_COUNTRIES.filter(country => !SKIP_COUNTRIES.includes(country.value))

  return (
    <Formik<WholesaleRegistrationFormData>
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form>
        <FormikErrors errors={errors} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormikTextField
              name={'email' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.email', 'Email')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormikTextField
              name={'storeName' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.storeName', 'Store name')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormikTextField
              name={'websiteUrl' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.websiteUrl', 'Website URL')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormikAutocomplete
              name={'brands' as keyof WholesaleRegistrationFormData}
              renderInput={() => null}
              options={BRANDS.map(o => o.value) || []}
              getOptionLabel={option => BRANDS.find(m => m.value === option)?.label || ''}
              isOptionEqualToValue={(option, value) => option === value}
              textFieldProps={{
                label: t('formLabels.wholesaleBrands', 'Please tell us which brands you’re interested in'),
              }}
              disableClearable={false}
              multiple
              isDefaultRenderInput
            />
          </Grid>
          <Grid item xs={12}>
            <FormikAutocomplete
              name={'country' as keyof WholesaleRegistrationFormData}
              renderInput={() => null}
              options={isoCountriesOptions.map(o => o.value) || []}
              renderOption={(props, option: string) => (
                <li {...props}>
                  <span className='pr-10'>{countryToFlag(option)}</span>
                  {isoCountriesOptions.find(m => m.value === option)?.label || ''}
                </li>
              )}
              disableClearable
              getOptionLabel={option => isoCountriesOptions.find(m => m.value === option)?.label || ''}
              isOptionEqualToValue={(option, value) => option === value}
              textFieldProps={{
                label: t('formLabels.country', 'Country'),
                required: true,
              }}
              isDefaultRenderInput
            />
          </Grid>
          <Grid item xs={12}>
            <FormikTextField
              name={'city' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.city', 'City')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormikTextField
              name={'firstName' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.firstName', 'First name')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormikTextField
              name={'lastName' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.lastName', 'Last name')}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormikAutocomplete
              name={'storeType' as keyof WholesaleRegistrationFormData}
              renderInput={() => null}
              options={STORE_TYPES.map(o => o.value) || []}
              getOptionLabel={option => STORE_TYPES.find(m => m.value === option)?.label || ''}
              isOptionEqualToValue={(option, value) => option === value}
              textFieldProps={{
                label: t('formLabels.storeType', 'What type is your store?'),
                required: true,
              }}
              isDefaultRenderInput
            />
          </Grid>

          <Grid item xs={12}>
            <FormikAutocomplete
              name={'storeYear' as keyof WholesaleRegistrationFormData}
              renderInput={() => null}
              options={YEARS.map(o => o.value) || []}
              getOptionLabel={option => YEARS.find(m => m.value === option)?.label || ''}
              isOptionEqualToValue={(option, value) => option === value}
              textFieldProps={{
                label: t('formLabels.wholesaleStoreYear', 'When did your store launch (year)?'),
                required: true,
              }}
              isDefaultRenderInput
            />
          </Grid>

          <Grid item xs={12}>
            <FormikAutocomplete
              name={'storeDescription' as keyof WholesaleRegistrationFormData}
              renderInput={() => null}
              options={STORE_DESCRIPTIONS.map(o => o.value) || []}
              getOptionLabel={option => STORE_DESCRIPTIONS.find(m => m.value === option)?.label || ''}
              isOptionEqualToValue={(option, value) => option === value}
              textFieldProps={{
                label: t('formLabels.wholesaleStoreDescription', 'Which of the following best describes your shop?'),
              }}
              disableClearable={false}
              multiple
              isDefaultRenderInput
            />
          </Grid>

          <Grid item xs={12}>
            <FormikTextField
              name={'instagramUrl' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.instagram', 'Instagram')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormikTextField
              name={'facebookUrl' as keyof WholesaleRegistrationFormData}
              label={t('formLabels.facebook', 'Facebook')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormikCheckbox
              name={'subscribe' as keyof WholesaleRegistrationFormData}
              formControlLabelProps={{
                label: t('formLabels.wholesaleSubscribe', {
                  defaultValue:
                    'By submitting this form, you agree to receive occasional updates about product launches and news from {{siteName}}. You can unsubscribe later if you wish',
                  siteName: SITE_NAME,
                }),
                control: null,
              }}
            />
          </Grid>
        </Grid>
        <div className='mt-10'>
          <Button type='submit' variant='contained' size='large' fullWidth>
            {t('button.submitForm', 'Submit Form')}
          </Button>
        </div>
      </Form>
    </Formik>
  )
}

export default WholesaleRegistrationForm
